<template>
  <!-- The arbitrary variants whiten Vuetify's fields and unchecked checkbox glyphs, which
       otherwise let the green panel show through. -->
  <div
    class="rounded-lg p-3 border [&_.v-field]:bg-white [&_.v-selection-control:not(.v-selection-control--dirty)_.v-icon]:bg-white [&_.v-selection-control:not(.v-selection-control--dirty)_.v-icon]:rounded-sm"
    :class="showFilters ? 'bg-green-50 border-green-200' : 'border-transparent'"
    data-cy="clients-filters"
  >
    <div class="flex flex-wrap items-start gap-5">
      <div class="flex flex-col" :class="{ 'min-w-[300px]': showFilters }">
        <!-- Pinned to the toggle's height so Clear All appearing beside it never pushes
             Search down; the 300px min-width leaves room for both buttons. -->
        <div class="flex items-center gap-2 h-7 mb-[5px]">
          <LlSecondaryButton
            variant="outlined"
            size="small"
            class="text-capitalize flex-shrink-0 !h-7"
            prepend-icon="mdi-filter-variant"
            :append-icon="showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            aria-label="Toggle filters"
            data-cy="clients-filters-toggle"
            @click="showFilters = !showFilters"
          >
            {{ showFilters ? '' : 'Filters' }}
          </LlSecondaryButton>
          <LlSecondaryButton
            v-if="showFilters && activeFilterCount > 0"
            variant="text"
            size="small"
            color="error"
            prepend-icon="mdi-close"
            class="!h-7"
            data-cy="clients-filters-clear-all-quiet"
            @click="emit('clear-filters')"
          >
            Clear All
          </LlSecondaryButton>
        </div>

        <template v-if="showFilters">
          <LlTextField
            v-model="filters.search"
            placeholder="Search Clients"
            prepend-inner-icon="mdi-magnify"
            data-cy="client-search-input"
            @update:model-value="handleSearch"
          />
          <!-- Only once "Validate QuickBooks Connection" has run: before that there is no
               connection status to filter on. -->
          <div v-if="filters.checkQuickbooks" class="mt-2">
            <LlSelect
              v-model="filters.qbConnectionType"
              :items="QB_CONNECTION_OPTIONS"
              item-title="title"
              item-value="value"
              :return-object="false"
              hide-details
              :loading="loading"
              data-cy="clients-filter-qb-connection"
              @update:model-value="applyFilters"
            />
          </div>
          <LlCheckbox
            v-if="canShowOrphans"
            v-model="filters.orphans"
            :label="`Show Clients without ${matterTerm}s`"
            class="mt-2"
            data-cy="clients-filter-orphans"
            @update:model-value="applyFilters"
          />
        </template>
      </div>

      <div v-if="!showFilters" class="flex items-center gap-3">
        <LlStatusBadge v-if="activeFilterCount > 0">
          {{ activeFilterCount }} {{ activeFilterCount === 1 ? 'filter is' : 'filters are' }} active
        </LlStatusBadge>
        <LlSecondaryButton
          v-if="activeFilterCount > 0"
          variant="text"
          size="small"
          color="error"
          prepend-icon="mdi-close"
          data-cy="clients-filters-clear-all"
          @click="emit('clear-filters')"
        >
          Clear All
        </LlSecondaryButton>
      </div>

      <template v-else>
        <!-- The server ignores scope when listing clients without matters, so the picker is
             locked rather than left looking like it still applies. `|| undefined` removes the
             attribute: where `inert` isn't a DOM property, Vue would write inert="false". -->
        <div
          class="min-w-[240px] [&_.tw]:w-full [&_.v-autocomplete]:w-full [&_.v-field]:w-full"
          :class="{ 'opacity-50': filters.orphans }"
          :inert="filters.orphans || undefined"
          data-cy="clients-filter-involving"
        >
          <MatterInvolvingFilter
            :form="filters"
            :horizontal="true"
            @matter-involving-change="applyFilters"
          />
        </div>

        <CustomFieldsFilter
          v-if="features?.customFieldFilters"
          :fields="customFields"
          :model-value="filters.clientCustomFields"
          @update:model-value="handleCustomFieldsChange"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { storeToRefs } from 'pinia';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';
import LlTextField from '@/components/LlTextField.vue';
import CustomFieldsFilter from '@/components/CustomFieldsFilter.vue';
import MatterInvolvingFilter from '@/components/MatterInvolvingFilter.vue';
import type { CustomFieldFilterSelections } from '@/models/customFieldFilterTypes';
import { useMeStore } from '@/services/UserService/useMeStore';
import { QB_CONNECTION_OPTIONS } from '../constants/clientsListConstants';
import { useClientsListStore } from '../stores/useClientsListStore';

const emit = defineEmits<{
  'apply-filters': [];
  'clear-filters': [];
}>();

const meStore = useMeStore();
const { me, features } = storeToRefs(meStore);
const clientsListStore = useClientsListStore();
const { filters, customFields, loading, activeFilterCount } = storeToRefs(clientsListStore);

const showFilters = ref(true);
const matterTerm = computed(() => me.value?.terms?.Matter || 'Matter');
const canShowOrphans = computed(() => me.value?.role !== 'timekeeper');

const applyFilters = () => {
  filters.value.page = 1;
  emit('apply-filters');
};

// Matches the Matters list: 300ms fired mid-word, 1000ms felt sluggish.
let searchTimeout: ReturnType<typeof setTimeout> | undefined;
const handleSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(applyFilters, 500);
};
onBeforeUnmount(() => clearTimeout(searchTimeout));

const handleCustomFieldsChange = (selections: CustomFieldFilterSelections) => {
  filters.value.clientCustomFields = selections;
  applyFilters();
};
</script>
